import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';

import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import NotificationComposer from './pages/NotificationComposer';
import Templates from './pages/Templates';
import Materials from './pages/Materials';
import MediaManager from './pages/MediaManager';
import HomepageBuilder from './pages/HomepageBuilderIntegrated';
import Analytics from './pages/Analytics';
import Users from './pages/Users';
import Settings from './pages/Settings';
import CategoryManager from './pages/CategoryManager';
import TopicManager from './pages/TopicManager';
import ChapterManager from './pages/ChapterManager';
import NewsManager from './pages/NewsManager';
import SubjectsArticles from './pages/SubjectsArticles';
import SubjectsHtmlEditor from './pages/SubjectsHtmlEditor';
import LessonBuilder from './pages/LessonBuilder';
import LessonsLibrary from './pages/LessonsLibrary';
import BillingSettings from './pages/BillingSettings';
import McQuizTestList from './pages/McQuizTestList';
import McQuizBankUpload from './pages/McQuizBankUpload';
import McQuizTestBuilder from './pages/McQuizTestBuilder';
import McQuizQuestionBrowser from './pages/McQuizQuestionBrowser';
import McQuizAnalytics from './pages/McQuizAnalytics';
import McQuizTestPreview from './pages/McQuizTestPreview';

import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import { AuthProvider } from './contexts/AuthContext';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
    },
  },
});

function App() {
  const [isDark, setIsDark] = useState(() => {
    return localStorage.getItem('theme') === 'dark';
  });

  useEffect(() => {
    if (isDark) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [isDark]);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Router>
          <div className={`min-h-screen ${isDark ? 'dark' : ''}`}>
            <Routes>
              <Route path="/login" element={<Login />} />
              
              <Route element={<ProtectedRoute />}>
                <Route element={<Layout isDark={isDark} setIsDark={setIsDark} />}>
                  <Route path="/" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/compose" element={<NotificationComposer />} />
                  <Route path="/templates" element={<Templates />} />
                  <Route path="/materials" element={<Materials />} />
                  <Route path="/media" element={<MediaManager />} />
                  <Route path="/media/categories" element={<CategoryManager />} />
                  <Route path="/homepage" element={<HomepageBuilder />} />
                  <Route path="/lessons" element={<LessonsLibrary />} />
                  <Route path="/lessons/new" element={<LessonBuilder />} />
                  <Route path="/lessons/:lessonId" element={<LessonBuilder />} />
                  <Route path="/analytics" element={<Analytics />} />
                  <Route path="/users" element={<Users />} />
                  <Route path="/billing" element={<BillingSettings />} />
                  <Route path="/settings" element={<Settings />} />
                  <Route path="/topics" element={<TopicManager />} />
                  <Route path="/chapters" element={<ChapterManager />} />
                  <Route path="/news" element={<NewsManager />} />
                  <Route path="/subjects" element={<SubjectsArticles />} />
                  <Route path="/subjects-html" element={<SubjectsHtmlEditor />} />
                  <Route path="/mcquiz/tests" element={<McQuizTestList />} />
                  <Route path="/mcquiz/tests/new" element={<McQuizTestBuilder />} />
                  <Route path="/mcquiz/tests/:testId" element={<McQuizTestBuilder />} />
                  <Route path="/mcquiz/tests/:testId/preview" element={<McQuizTestPreview />} />
                  <Route path="/mcquiz/banks" element={<McQuizBankUpload />} />
                  <Route path="/mcquiz/questions" element={<McQuizQuestionBrowser />} />
                  <Route path="/mcquiz/analytics" element={<McQuizAnalytics />} />
                </Route>
              </Route>
            </Routes>
            
            <Toaster
              position="top-right"
              toastOptions={{
                className: '',
                style: {
                  background: isDark ? '#1f2937' : '#ffffff',
                  color: isDark ? '#f3f4f6' : '#1f2937',
                  border: isDark ? '1px solid #374151' : '1px solid #e5e7eb',
                },
              }}
            />
          </div>
        </Router>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
